import React, { useMemo } from "react";
import { Box, SxProps } from "@mui/system";
import {
  MenuItem,
  Paper,
  TextField,
  Link as MuiLink,
  Tooltip,
  Button,
  FormGroup,
  FormHelperText,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { Loop } from "@mui/icons-material";

import useDelay from "decodable-ui/lib/hooks/useDelay";

import { Plan, termsOfServiceHref } from "../constants";
import {
  AccountCreateFormAction,
  AccountCreateFormState,
} from "../hooks/useAccountCreatePageState";
import { FadeTransition } from "./FadeTransition";

export type AccountCreateFormProps = {
  sx?: SxProps;
  onRefetchRegions: () => void;
  plan: Plan;
  formState: AccountCreateFormState;
  dispatch: (update: AccountCreateFormAction) => void;

  isCreating: boolean;
  onCreateAccount: () => void;
};

export function AccountCreateForm({
  sx,
  onRefetchRegions,
  plan,
  formState,
  dispatch,
  isCreating,
  onCreateAccount,
}: AccountCreateFormProps) {
  const title = useMemo(() => <>Create your {plan.name} account</>, [plan]);

  // We delay the loading message for regions for 1 second to avoid jank
  // in the case that they resolve quickly, but still provide feedback in
  // case the response is slow.
  const { delayPassed: loadingDelayPassed } = useDelay(1000);

  const regionSelectorIsReady =
    !formState.regionsAreLoading && !Boolean(formState.regionsError);

  return (
    <Paper
      component={"form"}
      autoComplete="off"
      sx={{
        ...sx,
        display: "grid",
        "&>*": { padding: "2rem" },
      }}
      elevation={10}
      onSubmit={(e) => {
        e.preventDefault();
        onCreateAccount();
      }}
    >
      <Box
        sx={{
          fontWeight: "600",
          fontSize: "2rem",
          margin: 0,
          borderBottom: "solid 1px rgb(90% 90% 90%)",
        }}
      >
        <FadeTransition>{title}</FadeTransition>
      </Box>
      <Box
        sx={{
          display: "grid",
          gap: "2.5rem",
          paddingBottom: "0.8rem",
        }}
      >
        <FormGroup row sx={{ width: "100%" }}>
          <Box sx={{ display: "flex", width: "100%" }}>
            <TextField
              inputProps={{ "data-hj-allow": true }}
              autoFocus
              label="Display Name"
              placeholder={"Account Name"}
              value={formState.displayName}
              error={!!formState.displayNameErr}
              onChange={(e) =>
                dispatch({
                  type: "updateForm",
                  formData: { displayName: e.target.value },
                })
              }
              required
              sx={{
                flexGrow: 1,
                boxSizing: "border-box",
                "& .MuiInputBase-root *": { borderRadius: "4px 0 0 4px" },
              }}
            />
            <Tooltip title="Generate a new name">
              <Button
                variant="contained"
                color="secondary"
                sx={{
                  boxSizing: "border-box",
                  border: "solid 1px rgb(80% 80% 80%)",
                  borderLeft: "none",
                  borderRadius: "0 4px 4px 0",
                }}
                onClick={() =>
                  dispatch({
                    type: "regenerateName",
                  })
                }
                disableElevation
              >
                <Loop />
              </Button>
            </Tooltip>
          </Box>
          <FormHelperText
            sx={{
              // magic number to match the margin that TextField uses. Not sure why this isn't the default
              marginLeft: "14px",
            }}
          >
            {formState.displayNameErr ??
              "We’ll use this to refer to your account throughout this dashboard."}
          </FormHelperText>
        </FormGroup>
        <TextField
          inputProps={{ "data-hj-allow": true }}
          label="Account Name"
          helperText={
            formState.nameErr ??
            (formState.isNameConflict
              ? "Sorry, a Decodable Account already exists for this name."
              : "A unique name to identify your account when using the Decodable APIs or CLIs.")
          }
          placeholder={"account-name"}
          value={formState.name}
          error={formState.isNameConflict || !!formState.nameErr}
          onChange={(e) =>
            dispatch({
              type: "updateForm",
              formData: { name: e.target.value },
            })
          }
          required
        />
        <TextField
          label="Region"
          select={regionSelectorIsReady ? true : false}
          value={formState.selectedRegion}
          error={Boolean(formState.regionsError)}
          disabled={!regionSelectorIsReady}
          placeholder={
            !loadingDelayPassed
              ? ""
              : formState.regionsAreLoading
              ? "Loading available regions..."
              : "No regions available"
          }
          helperText={
            formState.regionsError ? (
              <span>
                Error loading available regions.{" "}
                <Button
                  sx={{
                    width: "min-content",
                    minWidth: "min-content",
                    margin: 0,
                    padding: "2px",
                    color: "inherit",
                    fontSize: "inherit",
                    textDecoration: "underline",
                    "&:hover": {
                      bgcolor: "inherit",
                      textDecoration: "underline",
                    },
                  }}
                  onClick={onRefetchRegions}
                >
                  Retry.
                </Button>
              </span>
            ) : (
              "Select a region that's as close to your data sources and destinations as possible to reduce network costs from other service providers."
            )
          }
          required
          onChange={(e) =>
            dispatch({
              type: "updateForm",
              formData: { selectedRegion: e.target.value },
            })
          }
        >
          {(formState.regions ?? []).map((region) => (
            <MenuItem key={region} value={region}>
              {region}
            </MenuItem>
          ))}
        </TextField>
        <Tooltip title={formState.submitDisabledReason ?? ""}>
          <Box>
            <LoadingButton
              type="submit"
              loading={isCreating}
              fullWidth
              color="primary"
              variant="contained"
              sx={{
                // todo: I really need to stop inlining this.
                bgcolor: "var(--de-color-blue)",
                "&:hover": { bgcolor: "var(--de-color-blue)" },
              }}
              disabled={!!formState.submitDisabledReason}
            >
              Create Account
            </LoadingButton>
          </Box>
        </Tooltip>
      </Box>
      <Box
        sx={{
          padding: "0 2rem 2rem 2rem",
          fontSize: "0.8rem",
          margin: 0,
          color: "text.secondary",
          textAlign: "center",
        }}
      >
        By creating an account, you agree to Decodable's{" "}
        <MuiLink href={termsOfServiceHref}>Terms of Service</MuiLink>.
      </Box>
    </Paper>
  );
}
